/**
 * Automation engine.
 *
 * Subscribes to the workspace store. On every state change, diffs the
 * previous snapshot and dispatches matching automations. Also runs a
 * once-per-day scan for date_arrives triggers when the app loads.
 *
 * Runs all effects through the activity + workspace stores so mutations
 * remain visible to the rest of the app (and persist).
 */
import { useWorkspaceStore, PEOPLE } from '@/stores/workspaceStore';
import {
  useAutomationStore, type Automation, type Action, type Trigger,
} from '@/stores/automationStore';
import { useActivityStore } from '@/stores/activityStore';
import type { Item } from '@/stores/workspaceStore';

let previousItems: Map<string, Item> | null = null;
let started = false;

function personById(id?: string) {
  return PEOPLE.find((p) => p.id === id);
}

function resolveStatusCol(boardId: string) {
  return useWorkspaceStore.getState().columns.find((c) => c.boardId === boardId && c.type === 'status');
}
function resolvePersonCol(boardId: string) {
  return useWorkspaceStore.getState().columns.find((c) => c.boardId === boardId && c.type === 'person');
}
function resolveDateCol(boardId: string) {
  return useWorkspaceStore.getState().columns.find((c) => c.boardId === boardId && c.type === 'date');
}

function runActions(automation: Automation, item: Item | null): string {
  const audit: string[] = [];
  const store = useWorkspaceStore.getState();
  const activity = useActivityStore.getState();

  for (const a of automation.actions) {
    audit.push(renderAction(a, item));
    try {
      executeAction(a, item, automation, store, activity);
    } catch (err) {
      audit.push(`  ↳ failed: ${(err as Error).message}`);
    }
  }
  return audit.join(' · ');
}

function executeAction(
  a: Action,
  item: Item | null,
  automation: Automation,
  store: ReturnType<typeof useWorkspaceStore.getState>,
  activity: ReturnType<typeof useActivityStore.getState>,
) {
  switch (a.type) {
    case 'notify_person': {
      if (!a.personId) return;
      activity.addNotification({
        userId: a.personId,
        kind: 'mention',
        title: `Automation: ${automation.name}`,
        body: a.message ?? `${item?.name ?? 'An item'} triggered this automation.`,
        itemId: item?.id,
        boardId: automation.boardId,
      });
      return;
    }
    case 'set_status': {
      if (!item || !a.status) return;
      const col = resolveStatusCol(automation.boardId);
      if (col) store.updateItemValue(item.id, col.id, a.status);
      return;
    }
    case 'move_to_group': {
      if (!item || !a.groupId) return;
      store.moveItem(item.id, a.groupId);
      return;
    }
    case 'post_update': {
      if (!item) return;
      activity.addUpdate(item.id, a.message ?? 'Automated update.');
      return;
    }
    case 'create_item': {
      const boardId = a.boardId ?? automation.boardId;
      const groups = store.groups.filter((g) => g.boardId === boardId);
      const groupId = a.groupId ?? groups[0]?.id;
      if (!groupId) return;
      const name = (a.nameTemplate ?? 'Automated: {item_name}').replace('{item_name}', item?.name ?? 'new item');
      store.createItem(boardId, groupId, name);
      return;
    }
  }
}

function renderAction(a: Action, item: Item | null): string {
  switch (a.type) {
    case 'notify_person':  return `notify ${personById(a.personId)?.name ?? 'someone'}`;
    case 'set_status':     return `set "${item?.name ?? 'item'}" status to ${a.status}`;
    case 'move_to_group':  return `move "${item?.name ?? 'item'}" to another group`;
    case 'post_update':    return `post update on "${item?.name ?? 'item'}"`;
    case 'create_item':    return `create new item`;
  }
}

function triggerMatches(t: Trigger, ctx: {
  kind: 'item_created' | 'status_changed' | 'person_changed';
  boardId: string;
  item: Item;
  prev?: Item;
}): boolean {
  if (t.boardId && t.boardId !== ctx.boardId) return false;

  if (t.type === 'item_created' && ctx.kind === 'item_created') return true;

  if (t.type === 'status_changes_to' && ctx.kind === 'status_changed') {
    const col = resolveStatusCol(ctx.boardId);
    if (!col) return false;
    const nextVal = ctx.item.values[col.id] as string | undefined;
    return !t.status || nextVal === t.status;
  }

  if (t.type === 'person_assigned' && ctx.kind === 'person_changed') {
    const col = resolvePersonCol(ctx.boardId);
    if (!col) return false;
    const nextVal = ctx.item.values[col.id] as string | undefined;
    if (!nextVal) return false; // only fire on assign, not unassign
    return !t.personId || nextVal === t.personId;
  }

  return false;
}

function triggerMatchesDate(a: Automation, item: Item): boolean {
  if (a.trigger.type !== 'date_arrives') return false;
  const col = resolveDateCol(a.boardId);
  if (!col) return false;
  const dateStr = item.values[col.id] as string | undefined;
  if (!dateStr) return false;

  const due = new Date(dateStr); due.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const lead = a.trigger.leadDays ?? 0;
  const target = new Date(today); target.setDate(today.getDate() + lead);
  return due.getTime() === target.getTime();
}

function handleMutation(prevItems: Map<string, Item>, nextItems: Item[]) {
  const autos = useAutomationStore.getState().automations.filter((a) => a.isEnabled);
  if (autos.length === 0) return;

  const record = useAutomationStore.getState().recordRun;
  const nextIds = new Set(nextItems.map((i) => i.id));

  // Detect creates
  for (const it of nextItems) {
    if (!prevItems.has(it.id)) {
      for (const a of autos) {
        const matched = triggerMatches(a.trigger, { kind: 'item_created', boardId: it.boardId, item: it });
        if (matched) {
          const detail = runActions(a, it);
          record(a.id, 'success', { output: detail });
        }
      }
    }
  }

  // Detect column-value changes (status, person) on existing items
  for (const it of nextItems) {
    const prev = prevItems.get(it.id);
    if (!prev) continue;
    if (prev === it) continue;

    const statusCol = resolveStatusCol(it.boardId);
    if (statusCol && prev.values[statusCol.id] !== it.values[statusCol.id]) {
      for (const a of autos) {
        if (triggerMatches(a.trigger, { kind: 'status_changed', boardId: it.boardId, item: it, prev })) {
          const detail = runActions(a, it);
          record(a.id, 'success', { output: detail });
        }
      }
    }

    const personCol = resolvePersonCol(it.boardId);
    if (personCol && prev.values[personCol.id] !== it.values[personCol.id]) {
      for (const a of autos) {
        if (triggerMatches(a.trigger, { kind: 'person_changed', boardId: it.boardId, item: it, prev })) {
          const detail = runActions(a, it);
          record(a.id, 'success', { output: detail });
        }
      }
    }
  }

  // Detect deletes (unused for now, but keeping parity with create loop)
  void nextIds;
}

// ===== Date scan (runs once at startup + nightly via interval) =====
function runDateScan() {
  const autos = useAutomationStore.getState().automations
    .filter((a) => a.isEnabled && a.trigger.type === 'date_arrives');
  if (autos.length === 0) return;

  const items = useWorkspaceStore.getState().items;
  const record = useAutomationStore.getState().recordRun;
  const today = new Date().toDateString();
  const scanKey = `myday-date-scan-${today}`;
  if (sessionStorage.getItem(scanKey)) return;
  sessionStorage.setItem(scanKey, '1');

  for (const a of autos) {
    const scoped = items.filter((it) => it.boardId === a.boardId);
    for (const it of scoped) {
      if (triggerMatchesDate(a, it)) {
        const detail = runActions(a, it);
        record(a.id, 'success', { output: detail });
      }
    }
  }
}

/** Reset the baseline so the next store change isn't seen as "all items new".
 *  Called after Supabase hydration populates the store. */
export function resyncAutomationBaseline() {
  previousItems = new Map(useWorkspaceStore.getState().items.map((i) => [i.id, i]));
}

export function startAutomationRunner() {
  if (started) return;
  started = true;

  previousItems = new Map(useWorkspaceStore.getState().items.map((i) => [i.id, i]));

  useWorkspaceStore.subscribe((state, prev) => {
    // Skip if items array reference unchanged
    if (state.items === prev.items) return;
    const nextMap = new Map(state.items.map((i) => [i.id, i]));

    // Safety: if the set of item ids changed dramatically (first hydration),
    // treat it as a baseline reset and skip the fan-out — otherwise we loop
    // firing `item_created` for every hydrated item.
    if (previousItems) {
      const added = state.items.filter((it) => !previousItems!.has(it.id)).length;
      const removed = Array.from(previousItems.keys()).filter((id) => !nextMap.has(id)).length;
      const bulkSwap = added > 5 || removed > 5;
      if (bulkSwap) {
        previousItems = nextMap;
        return;
      }
      handleMutation(previousItems, state.items);
    }
    previousItems = nextMap;
  });

  // Date scan after a short delay (give stores time to rehydrate)
  setTimeout(runDateScan, 1500);
  // And roughly hourly while app is open
  setInterval(runDateScan, 60 * 60 * 1000);
}
